import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import moment, { type Moment } from 'moment';
import { useMemo, useState } from 'react';

import DatePickerWithShortcuts from '../../../components/UI/DatePickerWithShortcuts';
import { useGetAdminBusinessUnitsQuery } from '../../../services/api/adminBusinessUnits.api';
import {
  type FailedByErrorItem,
  type FailedByTemplateNameItem,
  useGetWaStatsQuery,
} from '../../../services/api/waStats.api';

const DETAILS_PAGE_SIZE = 8;

/**
 * Tab content for Meta / WhatsApp status monitoring.
 * Shows: templates sent (filtered by date range), % success, failed templates by error code, 360dialog health.
 * Metrics are fetched only when the user clicks "Obtener métricas".
 */
const MetaStatus = () => {
  const theme = useTheme();
  const [startDate, setStartDate] = useState<Moment | null>(() => moment().subtract(6, 'days').startOf('day'));
  const [endDate, setEndDate] = useState<Moment | null>(() => moment().endOf('day'));
  const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useState<number | null>(null);
  const [fetchParams, setFetchParams] = useState<{
    startDate: string;
    endDate: string;
    businessUnitId: number | null;
  } | null>(null);
  const [failedCardExpanded, setFailedCardExpanded] = useState(false);
  const [visibleErrorsCount, setVisibleErrorsCount] = useState(DETAILS_PAGE_SIZE);
  const [visibleTemplatesCount, setVisibleTemplatesCount] = useState(DETAILS_PAGE_SIZE);

  const { data: businessUnits } = useGetAdminBusinessUnitsQuery(undefined);
  const { data, isLoading, error } = useGetWaStatsQuery(fetchParams ?? undefined, {
    skip: !fetchParams,
    refetchOnMountOrArgChange: true,
  });

  const handleDateRangeChange = (newStart: Moment | null, newEnd: Moment | null) => {
    setStartDate(newStart);
    setEndDate(newEnd);
  };

  const handleFetchMetrics = () => {
    if (startDate && endDate && !isLoading) {
      setVisibleErrorsCount(DETAILS_PAGE_SIZE);
      setVisibleTemplatesCount(DETAILS_PAGE_SIZE);
      setFetchParams({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        businessUnitId: selectedBusinessUnitId,
      });
    }
  };

  const hasFailedDetails = data && (data.failedByError.length > 0 || data.failedByTemplateName.length > 0);
  const canExpandFailedCard = !!data;

  const fetchedRangeLabel = useMemo(() => {
    if (!fetchParams) return null;
    const start = moment(fetchParams.startDate);
    const end = moment(fetchParams.endDate);
    return `${start.format('DD/MM/YYYY')} – ${end.format('DD/MM/YYYY')}`;
  }, [fetchParams]);

  const successPercent = useMemo(() => {
    if (!data) return 0;
    return Math.max(0, 100 - data.failedPercent);
  }, [data]);

  const trendChartData = useMemo(
    () =>
      data?.trend.map((point) => {
        const successful = point.templatesSent;
        const failed = point.templatesFailed;
        const total = successful + failed;
        const failedPercent = total > 0 ? Math.round((failed / total) * 10000) / 100 : 0;
        return {
          day: point.day,
          label: moment.utc(point.day, 'YYYY-MM-DD').format('DD/MM'),
          successful,
          failed,
          total,
          failedPercent,
        };
      }) ?? [],
    [data]
  );

  return (
    <Box
      sx={{
        py: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Card
        variant="outlined"
        sx={{
          borderRadius: 3,
          background:
            'linear-gradient(120deg, rgba(25,118,210,0.10) 0%, rgba(25,118,210,0.04) 45%, rgba(25,118,210,0.02) 100%)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'center' },
              flexDirection: { xs: 'column', md: 'row' },
              gap: 1.5,
            }}
          >
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                }}
              >
                Meta / WhatsApp Status
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mt: 0.5,
                }}
              >
                Monitorea envios de plantillas, tasa de fallas y agrupacion de errores.
              </Typography>
            </Box>
            {fetchedRangeLabel && <Chip color="primary" variant="outlined" label={`Rango: ${fetchedRangeLabel}`} />}
          </Box>
        </CardContent>
      </Card>
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <DatePickerWithShortcuts
              startDate={startDate}
              endDate={endDate}
              onChange={handleDateRangeChange}
              defaultOption="last7Days"
            />
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="meta-stats-bu-label">Unidad de negocio</InputLabel>
              <Select
                labelId="meta-stats-bu-label"
                value={selectedBusinessUnitId ?? ''}
                label="Unidad de negocio"
                onChange={(e) =>
                  setSelectedBusinessUnitId(String(e.target.value) === '' ? null : Number(e.target.value))
                }
              >
                <MenuItem value="">Todas</MenuItem>
                {(businessUnits ?? []).map((bu) => (
                  <MenuItem key={bu.id} value={bu.id}>
                    {bu.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleFetchMetrics}
              disabled={!startDate || !endDate || isLoading}
              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ minWidth: 170 }}
            >
              {isLoading ? 'Cargando...' : 'Obtener metricas'}
            </Button>
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              mt: 1.5,
              display: 'block',
            }}
          >
            Sin unidad seleccionada se muestran todas. Las metricas se actualizan al presionar &quot;Obtener
            metricas&quot;.
          </Typography>
        </CardContent>
      </Card>
      {!fetchParams && (
        <Alert severity="info" variant="outlined">
          Selecciona un rango de fechas y pulsa &quot;Obtener metricas&quot; para cargar el dashboard.
        </Alert>
      )}
      {fetchParams && isLoading && (
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Cargando metricas...</Typography>
          </CardContent>
        </Card>
      )}
      {fetchParams && error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          No se pudo cargar las metricas de plantillas.
        </Alert>
      )}
      {fetchParams && !isLoading && !error && data !== undefined && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Card variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                }}
              >
                Evolucion diaria
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mb: 2,
                }}
              >
                Barras apiladas por dia: azul para envios no fallidos y rojo para fallas.
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  mb: 2,
                }}
              >
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`Enviadas: ${data.templatesSent.toLocaleString()}`}
                />
                <Chip
                  size="small"
                  color="error"
                  variant="outlined"
                  label={`Fallidas: ${data.templatesFailed.toLocaleString()}`}
                />
              </Box>
              <Box sx={{ height: 320, width: '100%' }}>
                <BarChart
                  dataset={trendChartData}
                  xAxis={[
                    {
                      scaleType: 'band',
                      dataKey: 'label',
                      tickLabelStyle: {
                        angle: trendChartData.length > 14 ? 45 : 0,
                        textAnchor: trendChartData.length > 14 ? 'start' : 'middle',
                        fontSize: 11,
                      },
                    },
                  ]}
                  series={[
                    {
                      dataKey: 'successful',
                      label: 'Exitosas',
                      stack: 'total',
                      color: theme.palette.primary.light,
                      valueFormatter: (value: number | null, { dataIndex }: { dataIndex: number }) => {
                        const row = trendChartData[dataIndex];
                        return `${value ?? 0} exitosas de ${row?.total ?? 0} enviadas`;
                      },
                    },
                    {
                      dataKey: 'failed',
                      label: 'Fallidas',
                      stack: 'total',
                      color: theme.palette.error.main,
                      valueFormatter: (value: number | null, { dataIndex }: { dataIndex: number }) => {
                        const row = trendChartData[dataIndex];
                        const total = row?.total ?? 0;
                        const failed = value ?? 0;
                        const failedRate = total > 0 ? (failed * 100) / total : 0;
                        return `${failed} fallidas (${failedRate.toFixed(1)}%)`;
                      },
                    },
                  ]}
                  yAxis={[
                    {
                      min: 0,
                      tickMinStep: 1,
                    },
                  ]}
                  height={320}
                  margin={{
                    top: 48,
                    right: 20,
                    bottom: trendChartData.length > 14 ? 80 : 40,
                    left: 40,
                  }}
                  slotProps={{
                    legend: {
                      direction: 'horizontal',

                      position: {
                        vertical: 'top',
                        horizontal: 'center',
                      },
                    },
                  }}
                />
              </Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mt: 3,
                  mb: 1,
                }}
              >
                Tasa de falla por dia (%)
              </Typography>
              <Box sx={{ height: 240, width: '100%' }}>
                <LineChart
                  series={[
                    {
                      data: trendChartData.map((d) => d.failedPercent),
                      label: 'Tasa de falla (%)',
                      color: theme.palette.error.main,
                      valueFormatter: (value: number | null) => (value != null ? `${value}%` : '—'),
                      showMark: true,
                    },
                  ]}
                  xAxis={[
                    {
                      scaleType: 'point',
                      data: trendChartData.map((d) => d.label),
                      tickLabelStyle: {
                        angle: trendChartData.length > 14 ? 45 : 0,
                        textAnchor: trendChartData.length > 14 ? 'start' : 'middle',
                        fontSize: 11,
                      },
                    },
                  ]}
                  yAxis={[
                    {
                      min: 0,
                      max: 100,
                      tickMinStep: 10,
                      valueFormatter: (value: number | null) => (value != null ? `${value}%` : ''),
                    },
                  ]}
                  height={240}
                  margin={{
                    top: 44,
                    right: 20,
                    bottom: trendChartData.length > 14 ? 80 : 40,
                    left: 44,
                  }}
                  slotProps={{
                    legend: {
                      direction: 'horizontal',

                      position: {
                        vertical: 'top',
                        horizontal: 'center',
                      },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 1,
                }}
              >
                <Box>
                  <Typography
                    variant="overline"
                    sx={{
                      color: 'text.secondary',
                      letterSpacing: 0.5,
                    }}
                  >
                    Plantillas enviadas
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    {data.templatesSent.toLocaleString()}
                  </Typography>
                </Box>
                <SendOutlinedIcon color="primary" />
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Total de envios de plantilla en el rango consultado.
              </Typography>
            </CardContent>
          </Card>

          <Card
            variant="outlined"
            sx={{
              borderRadius: 3,
              borderColor: hasFailedDetails ? 'error.light' : 'divider',
              ...(canExpandFailedCard && {
                cursor: 'pointer',
                '&:hover': { bgcolor: 'error.50' },
              }),
            }}
            onClick={() => canExpandFailedCard && setFailedCardExpanded((e) => !e)}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 1,
                }}
              >
                <Box>
                  <Typography
                    variant="overline"
                    sx={{
                      color: 'text.secondary',
                      letterSpacing: 0.5,
                    }}
                  >
                    Plantillas fallidas
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: 'error.main',
                    }}
                  >
                    {data.templatesFailed.toLocaleString()}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <ErrorOutlineIcon color="error" />
                  {canExpandFailedCard &&
                    (failedCardExpanded ? <ExpandLessIcon color="action" /> : <ExpandMoreIcon color="action" />)}
                </Box>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mb: 1.2,
                }}
              >
                {data.failedPercent}% del total enviado
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, Math.max(0, data.failedPercent))}
                color="error"
                sx={{ height: 8, borderRadius: 999 }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  mt: 1,
                  display: 'block',
                }}
              >
                {successPercent.toFixed(2)}% de envios no fallaron
              </Typography>
            </CardContent>
            <Collapse in={failedCardExpanded} timeout="auto" unmountOnExit>
              <Divider />
              <Box
                sx={{
                  px: 2.5,
                  pb: 2.5,
                  pt: 1.5,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 3,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Errores agrupados
                  </Typography>
                  {data.failedByError.length === 0 ? (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      Sin errores agrupados en el rango.
                    </Typography>
                  ) : (
                    <>
                      {data.failedByError.slice(0, visibleErrorsCount).map((item: FailedByErrorItem) => (
                        <Box
                          key={`${item.error}-${item.count}`}
                          sx={{
                            py: 1.1,
                            borderBottom: 1,
                            borderColor: 'divider',
                            '&:last-child': { borderBottom: 0 },
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <Chip
                              size="small"
                              color="error"
                              variant="outlined"
                              label={item.count.toLocaleString()}
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              wordBreak: 'break-word',
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                            }}
                            title={item.error}
                          >
                            {item.error}
                          </Typography>
                        </Box>
                      ))}
                      {data.failedByError.length > DETAILS_PAGE_SIZE && (
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1,
                            flexWrap: 'wrap',
                            mt: 1.5,
                          }}
                        >
                          {visibleErrorsCount < data.failedByError.length && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setVisibleErrorsCount((current) => current + DETAILS_PAGE_SIZE)}
                            >
                              Ver mas
                            </Button>
                          )}
                          {visibleErrorsCount > DETAILS_PAGE_SIZE && (
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => setVisibleErrorsCount(DETAILS_PAGE_SIZE)}
                            >
                              Ver menos
                            </Button>
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Plantillas con mas fallos
                  </Typography>
                  {data.failedByTemplateName.length === 0 ? (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      Sin plantillas con fallos en el rango.
                    </Typography>
                  ) : (
                    <>
                      {data.failedByTemplateName
                        .slice(0, visibleTemplatesCount)
                        .map((item: FailedByTemplateNameItem) => (
                          <Box
                            key={`${item.templateName}-${item.businessUnitId ?? ''}-${item.businessUnitName ?? ''}-${
                              item.count
                            }`}
                            sx={{
                              py: 1.1,
                              borderBottom: 1,
                              borderColor: 'divider',
                              '&:last-child': { borderBottom: 0 },
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Chip
                              size="small"
                              color="error"
                              variant="outlined"
                              label={item.count.toLocaleString()}
                              sx={{ fontWeight: 600, flexShrink: 0 }}
                            />
                            <Typography variant="body2" sx={{ wordBreak: 'break-word', flex: 1 }}>
                              {item.templateName}
                              {item.businessUnitName != null && item.businessUnitName !== '' && (
                                <Typography
                                  component="span"
                                  variant="body2"
                                  sx={{
                                    color: 'text.secondary',
                                    fontWeight: 400,
                                  }}
                                >
                                  {' — '}
                                  {item.businessUnitName}
                                </Typography>
                              )}
                            </Typography>
                          </Box>
                        ))}
                      {data.failedByTemplateName.length > DETAILS_PAGE_SIZE && (
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1,
                            flexWrap: 'wrap',
                            mt: 1.5,
                          }}
                        >
                          {visibleTemplatesCount < data.failedByTemplateName.length && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setVisibleTemplatesCount((current) => current + DETAILS_PAGE_SIZE)}
                            >
                              Ver mas
                            </Button>
                          )}
                          {visibleTemplatesCount > DETAILS_PAGE_SIZE && (
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => setVisibleTemplatesCount(DETAILS_PAGE_SIZE)}
                            >
                              Ver menos
                            </Button>
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </Collapse>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default MetaStatus;
